import React, { useEffect, useState, useCallback } from "react";
import { Dropdown } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  Brain, Plus, MoreHorizontal, Upload, Download, Pencil, Trash2, Sparkles,
} from "lucide-react";
import * as XLSX from "xlsx";
import apiClient from "../../utils/apiClient";
import { can } from "../../utils/access";
import { techStacksList } from "../../utils/common";
import DataTable from "../Common/DataTable";
import {
  PageHeader, AppButton, AppBadge, AppCard, AppCardContent, SearchInput,
  ConfirmModal, EmptyState,
  FilterToolbar, FilterButton, ActiveFilterChips, FilterPanel, FilterSelectField,
  useListFilters,
} from "../ui";
import CommonToaster from "../Common/CommonToaster";
import AIQuestionsImportModal from "./AIQuestionsImportModal";
import GenerateAIQuestionsModal from "./GenerateAIQuestionsModal";
import { useAIBankHeaderActions } from "./aiBankHeaderContext";

/* ── Converted to Tailwind utilities in Phase 5 (2026-08-24) ──────────────────
 * AIQuestionsList.scss is deleted (TOKEN_CONTRACT §6.1). Five of its thirteen classes
 * were already dead by the time it was opened — `.aiq-actions`, `.aiq-filters`,
 * `.aiq-search`, `.aiq-mobile-toolbar`, `.aiq-mobile-filter` all belonged to the two
 * filter bars this file replaced with one `FilterToolbar` in the same commit.
 *
 * Two rules reach into markup this component does not own and are arbitrary variants
 * for that reason: react-bootstrap's `.dropdown-item` / `.dropdown-divider` inside the
 * row menu, and its `::after` caret on the trigger. The danger hover in that menu was
 * `rgba(239,68,68,.1)` + `#dc2626` — a hand-mixed red pair that `--danger-soft` /
 * `--danger-fg` already provide, correctly, in both themes. */
const ROW_MENU =
  "tw-bg-surface-raised tw-border tw-border-line tw-rounded-sm tw-p-1 tw-min-w-[160px] " +
  "tw-shadow-[0_8px_20px_var(--card-shadow)] " +
  "[&_.dropdown-item]:tw-rounded-[6px] [&_.dropdown-item]:tw-text-content " +
  "[&_.dropdown-item]:tw-text-[0.85rem] [&_.dropdown-item]:tw-px-2.5 " +
  "[&_.dropdown-item]:tw-py-[0.45rem] [&_.dropdown-item]:tw-flex " +
  "[&_.dropdown-item]:tw-items-center " +
  "[&_.dropdown-item:hover]:tw-bg-surface-hover [&_.dropdown-item:focus]:tw-bg-surface-hover " +
  "[&_.dropdown-item.text-danger:hover]:tw-bg-danger-soft " +
  "[&_.dropdown-item.text-danger:hover]:!tw-text-danger-fg " +
  "[&_.dropdown-item.text-danger:focus]:tw-bg-danger-soft " +
  "[&_.dropdown-item.text-danger:focus]:!tw-text-danger-fg " +
  "[&_.dropdown-divider]:tw-border-line [&_.dropdown-divider]:tw-my-1";

const ROW_TRIGGER =
  "tw-inline-flex tw-items-center tw-justify-center tw-w-[30px] tw-h-[30px] tw-p-0 " +
  "tw-border tw-border-transparent tw-bg-transparent tw-rounded-[6px] tw-text-content-muted " +
  "tw-cursor-pointer tw-transition-[background-color,color,border-color] " +
  "hover:tw-bg-surface-hover hover:tw-text-content hover:tw-border-line " +
  "focus-visible:tw-bg-surface-hover focus-visible:tw-text-content " +
  "focus-visible:tw-border-line focus-visible:tw-outline-none " +
  // react-bootstrap draws its own caret on a Dropdown.Toggle.
  "after:tw-hidden";

const defaultTechStacks = techStacksList.filter((t) => t !== "");

const SORT_OPTIONS = [
  { value: "newest", label: "Newest First" },
  { value: "oldest", label: "Oldest First" },
  { value: "question-asc", label: "Question A → Z" },
  { value: "question-desc", label: "Question Z → A" },
];

const DIFFICULTY_OPTIONS = [
  { value: "", label: "All Difficulties" },
  { value: "easy", label: "Easy" },
  { value: "medium", label: "Medium" },
  { value: "hard", label: "Hard" },
];

const DIFFICULTY_BADGE_COLOR = {
  easy: "emerald",
  medium: "amber",
  hard: "rose",
};

const PAGE_SIZE = 10;

const AIQuestionsList = ({ embedded = false }) => {
  const navigate = useNavigate();
  const canManage = can("ai.manage");

  const [questions, setQuestions] = useState([]);
  const [filters, setFilters] = useState({
    techStack: "", difficulty: "", search: "", sort: "newest",
  });
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(PAGE_SIZE);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);

  const [showImportModal, setShowImportModal] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [techStacks, setTechStacks] = useState(defaultTechStacks);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const showToast = (message, type = "success") =>
    setToast({ show: true, message, type });

  // ── Data fetching ──────────────────────────────────────────────
  const fetchTechStacks = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/ai/ai-questions/tech-stacks");
      const list = Array.isArray(data?.techStacks) ? data.techStacks : [];
      setTechStacks(list);
    } catch {
      /* keep defaults */
    }
  }, []);

  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/ai/ai-questions/list", {
        params: { page, limit: pageSize, ...filters },
      });
      setQuestions(data.questions || []);
      setTotalRecords(data.total || 0);
    } catch {
      setQuestions([]);
      setTotalRecords(0);
    }
    setLoading(false);
  }, [page, pageSize, filters]);

  useEffect(() => { fetchTechStacks(); }, [fetchTechStacks]);
  useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

  // Debounce search input → filters
  useEffect(() => {
    if (searchInput === filters.search) return;
    const t = setTimeout(() => {
      setFilters((f) => ({ ...f, search: searchInput }));
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // ── Filter helpers ─────────────────────────────────────────────
  const techStackOptions = [
    { value: "", label: "All Tech Stacks" },
    ...techStacks.map((t) => ({ value: t, label: t })),
  ];

  // ⚠️ Three different empty values on one screen, which is why the hook takes it
  // per key: `techStack` and `difficulty` are `""`, and `sort` is `"newest"` — a
  // sort is never absent, it just has a default, and the old activeFilterCount
  // counted it as active exactly when it differed from that default.
  const listFilters = useListFilters(
    {
      techStack: { label: "Category", options: techStackOptions, empty: "" },
      difficulty: { label: "Difficulty", options: DIFFICULTY_OPTIONS, empty: "" },
      sort: { label: "Sort", options: SORT_OPTIONS, empty: "newest" },
    },
    () => setPage(1),
  );

  // The drawer owns the three values; the page's existing `filters` object mirrors
  // them so the fetch, the export and the empty-state copy keep reading one shape.
  useEffect(() => {
    const { techStack, difficulty, sort } = listFilters.values;
    setFilters((f) => (
      f.techStack === techStack && f.difficulty === difficulty && f.sort === sort
        ? f
        : { ...f, techStack, difficulty, sort }
    ));
  }, [listFilters.values]);

  const handleResetFilters = () => {
    listFilters.reset();
    setSearchInput("");
    setPage(1);
  };

  // ── Export ─────────────────────────────────────────────────────
  const handleExport = async () => {
    setExporting(true);
    try {
      const { data } = await apiClient.get("/ai/ai-questions/export", {
        params: {
          techStack: filters.techStack || undefined,
          difficulty: filters.difficulty || undefined,
          search: filters.search || undefined,
        },
      });
      const rows = (data?.questions || []).map((q) => ({
        question: q.question,
        techStack: q.techStack,
        difficulty: q.difficulty,
        createdAt: q.createdAt ? new Date(q.createdAt).toISOString() : "",
      }));
      if (rows.length === 0) {
        showToast("No questions to export for the current filters.", "warning");
        return;
      }
      const ws = XLSX.utils.json_to_sheet(rows, {
        header: ["question", "techStack", "difficulty", "createdAt"],
      });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "AI Questions");
      const date = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `ai-questions-${date}.xlsx`);
      showToast(`Exported ${rows.length} question(s)`, "success");
      if (data?.capped) {
        showToast("Export was capped at 5000 rows. Apply filters to narrow.", "warning");
      }
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to export questions.", "error");
    }
    setExporting(false);
  };

  const handleImported = () => {
    fetchQuestions();
    fetchTechStacks();
  };

  // ── Delete ─────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteTarget?._id) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/ai/ai-questions/${deleteTarget._id}`);
      showToast("Question deleted", "success");
      setDeleteTarget(null);
      fetchQuestions();
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to delete question.", "error");
    }
    setDeleting(false);
  };

  // ── Table columns ──────────────────────────────────────────────
  const columns = [
    {
      key: "index",
      label: "#",
      width: "56px",
      render: (_, index) => (
        <span className="tw-text-content-subtle tw-tabular-nums tw-text-[0.85rem]">{(page - 1) * pageSize + index + 1}</span>
      ),
    },
    {
      key: "question",
      label: "Question",
      render: (q) => (
        <div className="tw-text-sm tw-text-content tw-line-clamp-2 tw-overflow-hidden tw-text-ellipsis tw-leading-[1.45] tw-break-words" title={q.question}>
          {q.question}
        </div>
      ),
    },
    {
      key: "techStack",
      label: "Category",
      width: "180px",
      render: (q) => (
        q.techStack ? (
          <AppBadge variant="secondary" size="sm">{q.techStack}</AppBadge>
        ) : <span className="text-muted">—</span>
      ),
    },
    {
      key: "difficulty",
      label: "Difficulty",
      width: "120px",
      render: (q) => (
        q.difficulty ? (
          <AppBadge color={DIFFICULTY_BADGE_COLOR[q.difficulty.toLowerCase()] || "blue"} size="sm">
            {q.difficulty.charAt(0).toUpperCase() + q.difficulty.slice(1)}
          </AppBadge>
        ) : <span className="text-muted">—</span>
      ),
    },
    ...(canManage ? [{
      key: "actions",
      label: "",
      width: "56px",
      render: (q) => (
        <div className="tw-flex tw-justify-end" onClick={(e) => e.stopPropagation()}>
          <Dropdown align="end">
            <Dropdown.Toggle
              as="button"
              type="button"
              /* bsPrefix REPLACES react-bootstrap's `.dropdown-toggle`, which is the
                 class that draws the caret. It is a suppressor, not a style hook —
                 the styling is in className — so it names nothing this app defines.
                 `after:tw-hidden` in ROW_TRIGGER covers the case where a future
                 react-bootstrap keeps the caret on a different class. */
              bsPrefix="row-toggle"
              className={ROW_TRIGGER}
              aria-label="Row actions"
            >
              <MoreHorizontal size={16} />
            </Dropdown.Toggle>
            <Dropdown.Menu className={ROW_MENU}>
              <Dropdown.Item onClick={() => navigate(`/ai-questions/edit/${q._id}`)}>
                <Pencil size={14} className="me-2" /> Edit
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item
                onClick={() => setDeleteTarget(q)}
                className="text-danger"
              >
                <Trash2 size={14} className="me-2" /> Delete
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      ),
    }] : []),
  ];

  // ── Header actions (Import → Export → New Question → Generate with AI) ──
  const primaryActions = (
    <>
      {canManage && (
        <AppButton variant="outline" size="sm" onClick={() => setShowImportModal(true)}>
          <Upload size={14} /> <span>Import</span>
        </AppButton>
      )}
      <AppButton variant="outline" size="sm" onClick={handleExport} disabled={exporting}>
        <Download size={14} /> <span>{exporting ? "Exporting..." : "Export"}</span>
      </AppButton>
      {canManage && (
        <AppButton variant="outline" size="sm" onClick={() => navigate("/ai-questions/new")}>
          <Plus size={14} /> <span>New Question</span>
        </AppButton>
      )}
      {canManage && (
        <AppButton variant="default" size="sm" onClick={() => setShowGenerate(true)}>
          <Sparkles size={14} /> <span>Generate with AI</span>
        </AppButton>
      )}
    </>
  );

  // When embedded inside AIBankPage, push actions into the parent PageHeader.
  useAIBankHeaderActions(embedded ? primaryActions : null, [embedded, exporting]);

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      {!embedded && (
        <PageHeader
          icon={Brain}
          title="AI Questions"
          description="Manage AI-powered interview questions"
          actions={primaryActions}
        />
      )}

      {/* Filters */}
      {/* One toolbar at every width — replaces the `d-none d-md-flex` FilterBar and
          the `d-md-none` mobile strip that used to sit beside it. */}
      <FilterToolbar
        search={
          <SearchInput
            value={searchInput}
            onChange={setSearchInput}
            placeholder="Search questions..."
          />
        }
        filters={<FilterButton count={listFilters.count} onClick={listFilters.open} />}
      />

      <ActiveFilterChips items={listFilters.activeFilters} onClearAll={handleResetFilters} />

      {/* Table */}
      <AppCard className="tw-m-0 tw-overflow-hidden">
        <AppCardContent className="tw-p-0 [&_.data-table-wrapper]:tw-bg-transparent [&_.data-table-wrapper]:tw-shadow-none [&_.data-table-wrapper]:tw-p-0">
          {!loading && questions.length === 0 ? (
            <EmptyState
              icon={<Brain size={36} />}
              title={
                filters.search || filters.techStack || filters.difficulty
                  ? "No matching questions"
                  : "No AI questions yet"
              }
              description={
                filters.search || filters.techStack || filters.difficulty
                  ? "Try clearing filters or use a different search term."
                  : "Create your first AI interview question to get started."
              }
              action={canManage && (
                <AppButton variant="default" onClick={() => navigate("/ai-questions/new")}>
                  <Plus size={16} /> New Question
                </AppButton>
              )}
            />
          ) : (
            <DataTable
              data={questions}
              columns={columns}
              loading={loading}
              hoverable
              pagination={{
                page,
                pageSize,
                total: totalRecords,
                onPageChange: setPage,
              }}
              emptyMessage="No AI questions found"
            />
          )}
        </AppCardContent>
      </AppCard>

      {/* Mobile filter modal */}
      <FilterPanel
        {...listFilters.panelProps}
        onReset={handleResetFilters}
        subtitle="Narrow the AI question bank."
      >
        <FilterSelectField {...listFilters.selectProps("techStack")} />
        <FilterSelectField {...listFilters.selectProps("difficulty")} />
        <FilterSelectField {...listFilters.selectProps("sort")} />
      </FilterPanel>

      <AIQuestionsImportModal
        show={showImportModal}
        onHide={() => setShowImportModal(false)}
        onImported={handleImported}
      />

      {showGenerate && (
        <GenerateAIQuestionsModal
          onClose={() => setShowGenerate(false)}
          onSaved={(msg) => {
            setShowGenerate(false);
            showToast(msg, "success");
            fetchQuestions();
            fetchTechStacks();
          }}
        />
      )}

      <ConfirmModal
        show={!!deleteTarget}
        onHide={() => !deleting && setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete question?"
        message={
          <>
            <p className="mb-2">This will permanently remove the question:</p>
            <p className="mb-0 fw-medium">{deleteTarget?.question?.slice(0, 160)}{deleteTarget?.question?.length > 160 ? "…" : ""}</p>
          </>
        }
        confirmText={deleting ? "Deleting..." : "Delete"}
        variant="danger"
        loading={deleting}
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default AIQuestionsList;
